import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import { type RequestedDataGroup } from '../../hooks/useRequestedData';
import { useTranslation } from '../../i18n/i18n';
import RequestedDataModal from '../RequestedDataModal';

type RequestedDataCardProps = {
  groups: RequestedDataGroup[];
  isIssuance: boolean;
  variant: 'mobile' | 'desktop';
};

/** Requested data card with truncated credential list and "Show all" pill button. */
export default function RequestedDataCard({
  groups,
  isIssuance,
  variant,
}: RequestedDataCardProps) {
  const { t } = useTranslation();
  const [showDataModal, setShowDataModal] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-sidebar-foreground leading-6">
        {isIssuance
          ? t('sidebar.issuedDataTitle')
          : t('sidebar.requestedDataTitle')}
      </h3>
      <div className="flex items-end gap-0 rounded-md border border-border bg-card p-2">
        <div className="flex flex-1 flex-col">
          {groups.slice(0, 3).map((group) => (
            <div
              key={group.name}
              className="flex flex-col gap-1 rounded-sm p-2"
            >
              <span className="text-sm font-semibold text-card-foreground">
                {group.name}
              </span>
              <span className="text-xs text-card-foreground">
                {group.fields
                  .slice(0, 3)
                  .map((f) => f.name)
                  .join(', ')}
                {group.fields.length > 3 && ', …'}
              </span>
            </div>
          ))}
        </div>
        <div className="shrink-0 p-2">
          <button
            type="button"
            onClick={() => setShowDataModal(true)}
            className="flex h-8 items-center gap-2 rounded-full border border-input bg-background px-4 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          >
            {t('sidebar.showAll')}
            <FontAwesomeIcon icon={faEye} className="size-4" />
          </button>
        </div>
      </div>
      <div className="flex items-start gap-2">
        <span className="text-sm italic text-muted-foreground leading-5">
          {isIssuance
            ? t('sidebar.issuanceReviewNotice')
            : t('sidebar.reviewNotice')}
        </span>
      </div>
      {showDataModal && (
        <RequestedDataModal
          groups={groups}
          variant={variant}
          onClose={() => setShowDataModal(false)}
        />
      )}
    </div>
  );
}
